import axios from 'axios'
import { useStore } from '@/store'
import { getActivePinia } from 'pinia'

// URL base do backend Spring Boot (rede_marketing)
const baseURL = process.env.VUE_APP_REDE_MARKETING_URL || 'https://diariorede.agilesaas.sbs/api/'

const redeMarketingApi = axios.create({ baseURL }, 5000)

redeMarketingApi.interceptors.request.use(
  (config) => {
    const pinia = getActivePinia()
    if (pinia) {
      const store = useStore(pinia)
      // JWT proprio do rede_marketing (obtido via /api/auth/login com o token Sanctum)
      const jwt = store.redeMarketingJwt
      if (jwt) {
        config.headers['Authorization'] = `Bearer ${jwt}`
      }
      // Para endpoints de sincronizacao que precisam do token Sanctum
      if (config.url && config.url.includes('/sync/')) {
        config.headers['X-Laravel-Token'] = store.token
      }
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Interceptor de resposta: em 401, tenta renovar o JWT via token Sanctum do Laravel
// e re-executa a requisicao original UMA unica vez.
let refreshingPromise = null

redeMarketingApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    if (!error.response || error.response.status !== 401 || original._retry) {
      return Promise.reject(error)
    }

    original._retry = true
    const pinia = getActivePinia()
    if (!pinia) return Promise.reject(error)
    const store = useStore(pinia)
    if (!store.token) return Promise.reject(error)

    try {
      if (!refreshingPromise) {
        refreshingPromise = redeMarketingApi
          .post('auth/login', { sanctumToken: store.token })
          .finally(() => {
            // Libera o lock assim que a promise resolve ou rejeita
            const p = refreshingPromise
            refreshingPromise = null
            return p
          })
      }
      const loginResp = await refreshingPromise
      store.setRedeMarketingJwt(loginResp.data.token)
      original.headers['Authorization'] = `Bearer ${loginResp.data.token}`
      return redeMarketingApi(original)
    } catch (refreshErr) {
      store.setRedeMarketingJwt(null)
      return Promise.reject(refreshErr)
    }
  },
)

export default {
  // ========== AUTH ==========
  Login(sanctumToken) {
    return redeMarketingApi.post('auth/login', { sanctumToken })
  },
  Me() {
    return redeMarketingApi.get('auth/me')
  },

  // ========== CONFIGURACAO ==========
  ObterConfiguracao() {
    return redeMarketingApi.get('configuracao-rede')
  },
  AtualizarConfiguracao(config) {
    return redeMarketingApi.put('configuracao-rede', config)
  },

  // ========== MEMBROS ==========
  ListarMembros({ busca = '', page = 0, size = 20 } = {}) {
    const params = new URLSearchParams({ page, size })
    if (busca) params.append('busca', busca)
    return redeMarketingApi.get(`membros?${params.toString()}`)
  },
  CriarMembro(data) {
    return redeMarketingApi.post('membros', data)
  },
  BuscarMembro(id) {
    return redeMarketingApi.get(`membros/${id}`)
  },
  BuscarMembroPorLaravelId(laravelUserId) {
    return redeMarketingApi.get(`membros/laravel/${laravelUserId}`)
  },
  AtivarMembro(id) {
    return redeMarketingApi.patch(`membros/${id}/ativar`)
  },
  SincronizarCoaches() {
    return redeMarketingApi.post('membros/sync/coaches')
  },

  // ========== GENEALOGIA ==========
  BuscarNo(membroId) {
    return redeMarketingApi.get(`genealogia/${membroId}`)
  },
  Downline(membroId) {
    return redeMarketingApi.get(`genealogia/${membroId}/downline`)
  },
  Upline(membroId) {
    return redeMarketingApi.get(`genealogia/${membroId}/upline`)
  },
  ContarDiretos(membroId) {
    return redeMarketingApi.get(`genealogia/${membroId}/diretos/count`)
  },

  // ========== RANKS ==========
  ListarRanks() {
    return redeMarketingApi.get('ranks')
  },
  RankAtual(membroId) {
    return redeMarketingApi.get(`ranks/membro/${membroId}/atual`)
  },
  RecalcularRankMembro(membroId, ano, mes) {
    return redeMarketingApi.post(`ranks/recalcular/${membroId}?ano=${ano}&mes=${mes}`)
  },
  RecalcularTodos() {
    return redeMarketingApi.post('ranks/recalcular/todos')
  },
  CriarRank(data) {
    return redeMarketingApi.post('ranks', data)
  },
  AtualizarRank(id, data) {
    return redeMarketingApi.put(`ranks/${id}`, data)
  },
  ExcluirRank(id) {
    return redeMarketingApi.delete(`ranks/${id}`)
  },

  // ========== DASHBOARD ==========
  Resumo() {
    return redeMarketingApi.get('dashboard/resumo')
  },
}
